import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Trash2, Plus, Eye, EyeOff, Play, FlaskConical, Database } from 'lucide-react';
import { factoriesApi, brandsApi } from '../services/api';
import api from '../services/api';

const INTEGRATION_TYPES = [
  { value: '', label: '— нет —' },
  { value: 'osborne_little', label: 'Osborne & Little' },
  { value: 'sanderson_design', label: 'Sanderson Design Group' },
  { value: 'omexco', label: 'Omexco' },
  { value: 'khroma', label: 'Khroma' },
  { value: 'texdecor', label: 'Texdecor' },
  { value: 'elitis', label: 'Elitis' },
  { value: 'designers_guild', label: 'Designers Guild' },
  { value: 'pierre_frey', label: 'Pierre Frey' },
  { value: 'arte', label: 'Arte International' },
  { value: 'nobilis', label: 'Nobilis' },
  { value: 'colefax', label: 'Colefax & Fowler' },
  { value: 'thibaut', label: 'Thibaut' },
  { value: 'houles', label: 'Houlès' },
  { value: 'edesigntrade', label: 'e-designtrade (Kravet group)' },
];

const DEFAULT_CONFIGS = {
  osborne_little: {
    requests_per_second: 2,
  },
  sanderson_design: {
    batch_size: 50,
  },
  omexco: {
    request_delay: 0.3,
  },
  khroma: {
    request_delay: 0.5,
  },
  texdecor: {
    request_delay: 1.0,
  },
  elitis: {
    request_delay: 1.0,
  },
  designers_guild: {
    request_delay: 0.5,
  },
  pierre_frey: {
    request_delay: 1.0,
  },
  arte: {
    request_delay: 2.0,
  },
  nobilis: {
    request_delay: 0.5,
  },
  colefax: {
    request_delay: 0.5,
  },
  thibaut: {
    request_delay: 0.5,
  },
  houles: {
    request_delay: 0.5,
  },
  edesigntrade: {
    request_delay: 0.5,
  },
};

const EMPTY_FORM = {
  name: '',
  login_url: '',
  search_url: '',
  username: '',
  password: '',
  is_active: true,
  query_from_bot: false,
  brand_ids: [],
  integration_type: '',
  config: {},
};

export default function FactoriesPage() {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingFactory, setEditingFactory] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [brandSearch, setBrandSearch] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [configText, setConfigText] = useState('{}');
  const [configError, setConfigError] = useState('');
  const [showConfigEditor, setShowConfigEditor] = useState(false);

  // Test SKU modal state
  const [testModal, setTestModal] = useState(null); // { factoryId, factoryName }
  const [testSku, setTestSku] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);

  const { data: factories = [], isLoading } = useQuery({
    queryKey: ['factories'],
    queryFn: async () => (await factoriesApi.getAll()).data,
  });

  const { data: allBrands = [] } = useQuery({
    queryKey: ['brands'],
    queryFn: async () => (await brandsApi.getAll({ with_products_only: false })).data,
  });

  const createMutation = useMutation({
    mutationFn: (data) => factoriesApi.create(data),
    onSuccess: () => { queryClient.invalidateQueries(['factories']); closeModal(); toast.success('Фабрика создана'); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Ошибка создания'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => factoriesApi.update(id, data),
    onSuccess: () => { queryClient.invalidateQueries(['factories']); closeModal(); toast.success('Фабрика сохранена'); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Ошибка сохранения'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => factoriesApi.delete(id),
    onSuccess: () => { queryClient.invalidateQueries(['factories']); toast.success('Фабрика удалена'); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Ошибка удаления'),
  });

  const syncMutation = useMutation({
    mutationFn: (id) => api.post(`/factories/${id}/sync`),
    onSuccess: (res) => {
      toast.success(`Синхронизация запущена (задача ${res.data.task_id?.slice(0, 8)}...)`);
    },
    onError: (e) => toast.error(e.response?.data?.detail || 'Ошибка запуска синхронизации'),
  });

  const collectSkusMutation = useMutation({
    mutationFn: ({ id, retryMissing }) => api.post(`/factories/${id}/collect-skus`, { retry_missing: retryMissing }),
    onSuccess: (res) => {
      toast.success(`Сбор артикулов запущен (задача ${res.data.task_id?.slice(0, 8)}...)`);
    },
    onError: (e) => toast.error(e.response?.data?.detail || 'Ошибка запуска сбора артикулов'),
  });

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { if (testModal) setTestModal(null); else if (isModalOpen) closeModal(); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isModalOpen, testModal]);

  // Sync configText → formData.config
  useEffect(() => {
    try {
      const parsed = JSON.parse(configText);
      setFormData(f => ({ ...f, config: parsed }));
      setConfigError('');
    } catch {
      setConfigError('Невалидный JSON');
    }
  }, [configText]);

  const openCreate = () => {
    setEditingFactory(null);
    setFormData(EMPTY_FORM);
    setConfigText('{}');
    setConfigError('');
    setShowConfigEditor(false);
    setBrandSearch('');
    setShowPassword(false);
    setIsModalOpen(true);
  };

  const openEdit = (factory) => {
    setEditingFactory(factory);
    const config = factory.config || {};
    setFormData({
      name: factory.name,
      login_url: factory.login_url || '',
      search_url: factory.search_url || '',
      username: factory.username || '',
      password: '',
      is_active: factory.is_active,
      query_from_bot: factory.query_from_bot || false,
      brand_ids: factory.brand_ids || [],
      integration_type: factory.integration_type || '',
      config,
    });
    setConfigText(JSON.stringify(config, null, 2));
    setConfigError('');
    setShowConfigEditor(Object.keys(config).length > 0);
    setBrandSearch('');
    setShowPassword(false);
    setIsModalOpen(true);
  };

  const closeModal = () => { setIsModalOpen(false); setEditingFactory(null); };

  const handleIntegrationTypeChange = (value) => {
    setFormData(f => ({ ...f, integration_type: value }));
    if (value && DEFAULT_CONFIGS[value]) {
      const defaultCfg = DEFAULT_CONFIGS[value];
      setConfigText(JSON.stringify(defaultCfg, null, 2));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (configError) { toast.error('Исправьте JSON конфигурации'); return; }
    let config = {};
    try { config = JSON.parse(configText); } catch { toast.error('Ошибка в JSON конфигурации'); return; }
    const payload = { ...formData, config };
    if (editingFactory && !payload.password) delete payload.password;
    if (editingFactory) updateMutation.mutate({ id: editingFactory.id, data: payload });
    else createMutation.mutate(payload);
  };

  const handleDelete = (factory) => {
    if (!window.confirm(`Удалить фабрику "${factory.name}"?`)) return;
    deleteMutation.mutate(factory.id);
  };

  const toggleBrand = (brandId) => {
    const ids = formData.brand_ids;
    setFormData({ ...formData, brand_ids: ids.includes(brandId) ? ids.filter(id => id !== brandId) : [...ids, brandId] });
  };

  const handleTestSku = async () => {
    if (!testSku.trim()) return;
    setTestLoading(true);
    setTestResult(null);
    try {
      const res = await api.post(`/factories/${testModal.factoryId}/test-sku`, { sku: testSku.trim() });
      setTestResult(res.data);
    } catch (e) {
      setTestResult({ error: e.response?.data?.detail || e.message });
    } finally {
      setTestLoading(false);
    }
  };

  const filteredBrands = allBrands.filter(b =>
    !brandSearch || b.name.toLowerCase().includes(brandSearch.toLowerCase())
  ).sort((a, b) => {
    const aChecked = formData.brand_ids.includes(a.id) ? 1 : 0;
    const bChecked = formData.brand_ids.includes(b.id) ? 1 : 0;
    if (bChecked !== aChecked) return bChecked - aChecked;
    return a.name.localeCompare(b.name, 'ru');
  });

  const isSaving = createMutation.isPending || updateMutation.isPending;

  if (isLoading) return <div className="card"><div style={{ padding: '40px', textAlign: 'center' }}>Загрузка...</div></div>;

  return (
    <div>
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '4px' }}>Фабрики</h1>
            <div style={{ fontSize: '14px', color: '#6b7280' }}>
              Всего: <strong style={{ color: '#374151' }}>{factories.length}</strong>
            </div>
          </div>
          <button className="btn btn-primary flex items-center gap-2" onClick={openCreate}>
            <Plus size={16} /> Добавить фабрику
          </button>
        </div>

        {factories.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9ca3af' }}>Фабрики не добавлены</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  {['Название', 'Интеграция', 'Логин', 'Пароль', 'Бренды', 'Активна', ''].map(h => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {factories.map(factory => (
                  <tr
                    key={factory.id}
                    onClick={() => openEdit(factory)}
                    style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <td style={{ padding: '10px 12px', fontWeight: '600' }}>
                      {factory.name}
                      {factory.login_url && (
                        <div style={{ fontSize: '11px', color: '#9ca3af', fontWeight: '400', marginTop: '2px' }}>
                          <a href={factory.login_url} target="_blank" rel="noreferrer" style={{ color: '#2563eb' }}>{factory.login_url.replace(/^https?:\/\//, '')}</a>
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      {factory.integration_type ? (
                        <span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '2px 8px', borderRadius: '10px', fontSize: '12px', fontWeight: '600' }}>
                          {INTEGRATION_TYPES.find(t => t.value === factory.integration_type)?.label || factory.integration_type}
                        </span>
                      ) : <span style={{ color: '#9ca3af' }}>—</span>}
                    </td>
                    <td style={{ padding: '10px 12px' }}>{factory.username || '—'}</td>
                    <td style={{ padding: '10px 12px' }}>
                      {factory.has_password
                        ? <span style={{ color: '#059669', fontSize: '12px' }}>✓ задан</span>
                        : <span style={{ color: '#9ca3af', fontSize: '12px' }}>—</span>}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      {factory.brand_ids?.length > 0
                        ? <span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '2px 8px', borderRadius: '10px', fontSize: '12px', fontWeight: '600' }}>{factory.brand_ids.length} бр.</span>
                        : '—'}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ color: factory.is_active ? '#059669' : '#9ca3af', fontWeight: '600', fontSize: '13px' }}>
                        {factory.is_active ? 'Да' : 'Нет'}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px' }} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {factory.integration_type && (
                          <>
                            <button
                              title="Синхронизировать остатки"
                              onClick={() => syncMutation.mutate(factory.id)}
                              disabled={syncMutation.isPending}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#059669', padding: '4px' }}
                            >
                              <Play size={16} />
                            </button>
                            {factory.integration_type === 'sanderson_design' && (
                              <button
                                title="Собрать артикулы фабрики (factory_sku)"
                                onClick={() => collectSkusMutation.mutate({ id: factory.id, retryMissing: false })}
                                disabled={collectSkusMutation.isPending}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d97706', padding: '4px' }}
                              >
                                <Database size={16} />
                              </button>
                            )}
                            <button
                              title="Тест артикула"
                              onClick={() => { setTestModal({ factoryId: factory.id, factoryName: factory.name }); setTestSku(''); setTestResult(null); }}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7c3aed', padding: '4px' }}
                            >
                              <FlaskConical size={16} />
                            </button>
                          </>
                        )}
                        <button title="Удалить" onClick={() => handleDelete(factory)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '4px' }}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Factory modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '580px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header" style={{ flexShrink: 0 }}>
              <h2 className="modal-title">{editingFactory ? 'Редактировать фабрику' : 'Новая фабрика'}</h2>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>

                <div className="form-group">
                  <label>Название <span style={{ color: '#ef4444' }}>*</span></label>
                  <input className="input" required value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Osborne & Little" />
                </div>

                <div className="form-group">
                  <label>Тип интеграции</label>
                  <select className="input" value={formData.integration_type}
                    onChange={e => handleIntegrationTypeChange(e.target.value)}>
                    {INTEGRATION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>

                <div className="form-group">
                  <label>URL для логина</label>
                  <input className="input" type="url" value={formData.login_url}
                    onChange={e => setFormData({ ...formData, login_url: e.target.value })}
                    placeholder="https://example.com/api/v1/User/login" />
                </div>

                <div className="form-group">
                  <label>URL для поиска по артикулу</label>
                  <input className="input" type="url" value={formData.search_url}
                    onChange={e => setFormData({ ...formData, search_url: e.target.value })}
                    placeholder="https://example.com/api/v1/product/getbycode/" />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="form-group">
                    <label>Логин</label>
                    <input className="input" value={formData.username}
                      onChange={e => setFormData({ ...formData, username: e.target.value })}
                      placeholder="username" />
                  </div>
                  <div className="form-group">
                    <label>Пароль {editingFactory?.has_password && <span style={{ color: '#6b7280', fontSize: '12px', fontWeight: '400' }}>(оставьте пустым)</span>}</label>
                    <div style={{ position: 'relative' }}>
                      <input className="input" type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={e => setFormData({ ...formData, password: e.target.value })}
                        placeholder={editingFactory?.has_password ? '••••••••' : 'пароль'}
                        style={{ paddingRight: '36px' }} />
                      <button type="button" onClick={() => setShowPassword(v => !v)}
                        style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="form-group" style={{ display: 'flex', gap: '24px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input type="checkbox" checked={formData.is_active}
                      onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
                      style={{ marginRight: '8px' }} />
                    Активна
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input type="checkbox" checked={formData.query_from_bot}
                      onChange={e => setFormData({ ...formData, query_from_bot: e.target.checked })}
                      style={{ marginRight: '8px' }} />
                    Запрашивать из бота
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input type="checkbox"
                      checked={!!((() => { try { return JSON.parse(configText); } catch { return {}; } })().use_proxy)}
                      onChange={e => {
                        try {
                          const cfg = JSON.parse(configText);
                          cfg.use_proxy = e.target.checked;
                          setConfigText(JSON.stringify(cfg, null, 2));
                          setConfigError('');
                        } catch { setConfigError('Ошибка в JSON'); }
                      }}
                      style={{ marginRight: '8px' }} />
                    Residential proxy
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input type="checkbox"
                      checked={!!((() => { try { return JSON.parse(configText); } catch { return {}; } })().debug)}
                      onChange={e => {
                        try {
                          const cfg = JSON.parse(configText);
                          cfg.debug = e.target.checked;
                          setConfigText(JSON.stringify(cfg, null, 2));
                          setConfigError('');
                        } catch { setConfigError('Ошибка в JSON'); }
                      }}
                      style={{ marginRight: '8px' }} />
                    Дебаг
                  </label>
                  {formData.integration_type === 'sanderson_design' && (
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} title="При следующем запуске сбора SKU — перезаписать все артикулы заново (сбросится автоматически)">
                      <input type="checkbox"
                        checked={!!((() => { try { return JSON.parse(configText); } catch { return {}; } })().rebuild_skus)}
                        onChange={e => {
                          try {
                            const cfg = JSON.parse(configText);
                            cfg.rebuild_skus = e.target.checked;
                            setConfigText(JSON.stringify(cfg, null, 2));
                            setConfigError('');
                          } catch { setConfigError('Ошибка в JSON'); }
                        }}
                        style={{ marginRight: '8px' }} />
                      Пересобрать SKU
                    </label>
                  )}
                </div>

                {/* Config JSON */}
                <div className="form-group">
                  <button type="button"
                    onClick={() => setShowConfigEditor(v => !v)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb', fontSize: '13px', padding: '0', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {showConfigEditor ? '▼' : '▶'} Настройки конфигурации (JSON)
                  </button>
                  {showConfigEditor && (
                    <div style={{ marginTop: '8px' }}>
                      <textarea
                        value={configText}
                        onChange={e => setConfigText(e.target.value)}
                        rows={8}
                        style={{
                          width: '100%',
                          fontFamily: 'monospace',
                          fontSize: '12px',
                          border: configError ? '1px solid #ef4444' : '1px solid #d1d5db',
                          borderRadius: '6px',
                          padding: '8px',
                          resize: 'vertical',
                          boxSizing: 'border-box',
                        }}
                      />
                      {configError && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>{configError}</div>}
                      {formData.integration_type === 'osborne_little' && (
                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '6px', lineHeight: '1.7' }}>
                          <b>Параметры Osborne &amp; Little:</b><br />
                          <code>requests_per_second</code> — запросов в секунду (по умолч. <b>2</b>)<br />
                          <code>commit_batch_size</code> — коммит в БД каждые N артикулов (по умолч. <b>100</b>)<br />
                          <code>limit</code> — проверить только первые N артикулов, остальные пропустить (по умолч. все)<br />
                          <code>include_out_of_stock</code> — включать товары, которых уже нет на фабрике (по умолч. <b>false</b>)<br />
                          <code>debug</code> — подробные логи в консоль (по умолч. <b>false</b>)
                        </div>
                      )}
                      {formData.integration_type === 'sanderson_design' && (
                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '6px', lineHeight: '1.7' }}>
                          <b>Параметры Sanderson Design Group:</b><br />
                          <code>batch_size</code> — размер батча при опросе остатков (по умолч. <b>50</b>)<br />
                          <code>max_lookups_per_run</code> — максимум новых поисков factory_sku за один запуск (по умолч. <b>200</b>)<br />
                          <code>limit</code> — проверить только первые N артикулов (по умолч. все)<br />
                          <code>include_out_of_stock</code> — включать товары, которых уже нет на фабрике (по умолч. <b>false</b>)<br />
                          <code>debug</code> — подробные логи в консоль (по умолч. <b>false</b>)<br />
                          <b>Сбор SKU (отдельная кнопка):</b><br />
                          <code>sku_collect_delay</code> — пауза между запросами поиска, сек (по умолч. <b>0.2</b>)<br />
                          <code>collect_workers</code> — параллельных потоков поиска (по умолч. <b>5</b>)<br />
                          <code>rebuild_skus</code> — пересобрать все factory_sku заново (сбрасывается автоматически)<br />
                          Логин/пароль — от <b>trade.sandersondesigngroup.com</b>
                        </div>
                      )}
                      {formData.integration_type === 'omexco' && (
                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '6px', lineHeight: '1.7' }}>
                          <b>Параметры Omexco:</b><br />
                          <code>request_delay</code> — пауза между запросами, сек (по умолч. <b>0.3</b>)<br />
                          <code>include_out_of_stock</code> — включать товары, которых уже нет на фабрике (по умолч. <b>false</b>)<br />
                          <code>debug</code> — подробные логи в консоль (по умолч. <b>false</b>)<br />
                          Логин/пароль — от <b>extranet.omexco.be</b>
                        </div>
                      )}
                      {formData.integration_type === 'khroma' && (
                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '6px', lineHeight: '1.7' }}>
                          <b>Параметры Khroma:</b><br />
                          <code>request_delay</code> — пауза между запросами, сек (по умолч. <b>0.5</b>)<br />
                          <code>commit_batch_size</code> — коммит в БД каждые N артикулов (по умолч. <b>50</b>)<br />
                          <code>limit</code> — проверить только первые N артикулов (по умолч. все)<br />
                          <code>include_out_of_stock</code> — включать товары, которых уже нет на фабрике (по умолч. <b>false</b>)<br />
                          <code>debug</code> — подробные логи в консоль (по умолч. <b>false</b>)<br />
                          Остаток определяется порогами: 20 → 10 → 5 → 1 (3–4 запроса на артикул)<br />
                          Логин/пароль — от <b>stock.khroma.be</b>
                        </div>
                      )}
                      {formData.integration_type === 'texdecor' && (
                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '6px', lineHeight: '1.7' }}>
                          <b>Параметры Texdecor:</b><br />
                          <code>request_delay</code> — пауза между запросами, сек (по умолч. <b>1.0</b>)<br />
                          <code>commit_batch_size</code> — коммит в БД каждые N артикулов (по умолч. <b>50</b>)<br />
                          <code>limit</code> — проверить только первые N артикулов (по умолч. все)<br />
                          <code>include_out_of_stock</code> — включать товары, которых уже нет на фабрике (по умолч. <b>false</b>)<br />
                          <code>debug</code> — подробные логи в консоль (по умолч. <b>false</b>)<br />
                          Остаток в метрах (ML). Поиск по артикулу через каталог Magento 2.<br />
                          Логин/пароль — от <b>espacepro.texdecor.com</b>
                        </div>
                      )}
                      {formData.integration_type === 'elitis' && (
                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '6px', lineHeight: '1.7' }}>
                          <b>Параметры Elitis:</b><br />
                          <code>request_delay</code> — пауза между запросами, сек (по умолч. <b>1.0</b>)<br />
                          <code>commit_batch_size</code> — коммит в БД каждые N артикулов (по умолч. <b>50</b>)<br />
                          <code>limit</code> — проверить только первые N артикулов (по умолч. все)<br />
                          <code>include_out_of_stock</code> — включать товары, которых уже нет на фабрике (по умолч. <b>false</b>)<br />
                          <code>debug</code> — подробные логи в консоль (по умолч. <b>false</b>)<br />
                          Остаток в метрах (MT) или рулонах (Roll). Светофор: зелёный / оранжевый / красный.<br />
                          Логин/пароль — от <b>extranet.elitis.fr</b>
                        </div>
                      )}
                      {formData.integration_type === 'designers_guild' && (
                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '6px', lineHeight: '1.7' }}>
                          <b>Параметры Designers Guild:</b><br />
                          <code>request_delay</code> — пауза между запросами, сек (по умолч. <b>0.5</b>)<br />
                          <code>commit_batch_size</code> — коммит в БД каждые N артикулов (по умолч. <b>50</b>)<br />
                          <code>limit</code> — проверить только первые N артикулов (по умолч. все)<br />
                          <code>include_out_of_stock</code> — включать товары, которых уже нет на фабрике (по умолч. <b>false</b>)<br />
                          <code>debug</code> — подробные логи в консоль (по умолч. <b>false</b>)<br />
                          Ткань (PIECELEVEL=Y): остатки по кускам с метражом. Обои (N): суммарный метраж.<br />
                          Логин/пароль — от <b>trade.designersguild.com</b>
                        </div>
                      )}
                      {formData.integration_type === 'pierre_frey' && (
                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '6px', lineHeight: '1.7' }}>
                          <b>Параметры Pierre Frey:</b><br />
                          <code>request_delay</code> — пауза между запросами, сек (по умолч. <b>1.0</b>)<br />
                          <code>commit_batch_size</code> — коммит в БД каждые N артикулов (по умолч. <b>50</b>)<br />
                          <code>limit</code> — проверить только первые N артикулов (по умолч. все)<br />
                          <code>include_out_of_stock</code> — включать товары, которых уже нет на фабрике (по умолч. <b>false</b>)<br />
                          <code>debug</code> — подробные логи в консоль (по умолч. <b>false</b>)<br />
                          UUID ищется автоматически по артикулу и кешируется. Данные по кускам (м).<br />
                          Авторизация не нужна для получения данных. Логин/пароль — от <b>pierrefrey.com</b>
                        </div>
                      )}
                      {formData.integration_type === 'arte' && (
                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '6px', lineHeight: '1.7' }}>
                          <b>Параметры Arte International:</b><br />
                          <code>request_delay</code> — пауза между запросами, сек (по умолч. <b>2.0</b>)<br />
                          <code>commit_batch_size</code> — коммит в БД каждые N артикулов (по умолч. <b>50</b>)<br />
                          <code>limit</code> — проверить только первые N артикулов (по умолч. все)<br />
                          <code>include_out_of_stock</code> — включать товары, которых уже нет на фабрике (по умолч. <b>false</b>)<br />
                          <code>debug</code> — подробные логи в консоль (по умолч. <b>false</b>)<br />
                          Остаток в рулонах (RL). Показывается ближайшая дата поставки и её количество.<br />
                          Логин/пароль — от <b>b2b.arte-international.com</b>
                        </div>
                      )}
                      {formData.integration_type === 'nobilis' && (
                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '6px', lineHeight: '1.7' }}>
                          <b>Параметры Nobilis:</b><br />
                          <code>request_delay</code> — пауза между запросами, сек (по умолч. <b>0.5</b>)<br />
                          <code>commit_batch_size</code> — коммит в БД каждые N артикулов (по умолч. <b>50</b>)<br />
                          <code>limit</code> — проверить только первые N артикулов (по умолч. все)<br />
                          <code>include_out_of_stock</code> — включать товары, которых уже нет на фабрике (по умолч. <b>false</b>)<br />
                          <code>debug</code> — подробные логи в консоль (по умолч. <b>false</b>)<br />
                          <code>use_proxy</code> — ходить через резидентный прокси (по умолч. <b>false</b>)<br />
                          Остаток в метрах (M) или штуках (EA). Отрицательный остаток пишется как 0.<br />
                          Показывается ближайшая дата поставки и её количество. Логин/пароль — от <b>nobilis.fr/nova</b>
                        </div>
                      )}
                      {formData.integration_type === 'colefax' && (
                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '6px', lineHeight: '1.7' }}>
                          <b>Параметры Colefax &amp; Fowler:</b><br />
                          <code>request_delay</code> — пауза между запросами, сек (по умолч. <b>0.5</b>)<br />
                          <code>commit_batch_size</code> — коммит в БД каждые N артикулов (по умолч. <b>50</b>)<br />
                          <code>limit</code> — проверить только первые N артикулов (по умолч. все)<br />
                          <code>include_out_of_stock</code> — включать товары, которых уже нет на фабрике (по умолч. <b>false</b>)<br />
                          <code>debug</code> — подробные логи в консоль (по умолч. <b>false</b>)<br />
                          <code>use_proxy</code> — ходить через резидентный прокси (по умолч. <b>false</b>)<br />
                          Остаток в рулонах (Rolls) или метрах (Metres). Точное число до 99; при большем — «в наличии».<br />
                          Артикул со слешем (<code>07167/09</code>). Логин/пароль — от <b>trade.colefax.com</b>
                        </div>
                      )}
                      {formData.integration_type === 'thibaut' && (
                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '6px', lineHeight: '1.7' }}>
                          <b>Параметры Thibaut:</b><br />
                          <code>request_delay</code> — пауза между запросами, сек (по умолч. <b>0.5</b>)<br />
                          <code>commit_batch_size</code> — коммит в БД каждые N артикулов (по умолч. <b>50</b>)<br />
                          <code>limit</code> — проверить только первые N артикулов (по умолч. все)<br />
                          <code>include_out_of_stock</code> — включать товары, которых уже нет на фабрике (по умолч. <b>false</b>)<br />
                          <code>debug</code> — подробные логи в консоль (по умолч. <b>false</b>)<br />
                          <code>use_proxy</code> — ходить через резидентный прокси (по умолч. <b>false</b>)<br />
                          Остаток по лотам (метафилд inventory): обои — рулоны, ткань/тесьма — метры.<br />
                          Только текущий каталог US-сайта (~30% наших SKU); остальные — «не найден». Логин/пароль — от <b>thibautdesign.com</b>
                        </div>
                      )}
                      {formData.integration_type === 'houles' && (
                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '6px', lineHeight: '1.7' }}>
                          <b>Параметры Houlès:</b><br />
                          <code>request_delay</code> — пауза между запросами, сек (по умолч. <b>0.5</b>)<br />
                          <code>commit_batch_size</code> — коммит в БД каждые N артикулов (по умолч. <b>50</b>)<br />
                          <code>limit</code> — проверить только первые N артикулов (по умолч. все)<br />
                          <code>include_out_of_stock</code> — включать товары, которых уже нет на фабрике (по умолч. <b>false</b>)<br />
                          <code>debug</code> — подробные логи в консоль (по умолч. <b>false</b>)<br />
                          <code>use_proxy</code> — ходить через резидентный прокси (по умолч. <b>false</b>)<br />
                          Логин не нужен — остаток публичный (<b>houles.com</b>). Единицы: метр / шт (Piece, Card).
                        </div>
                      )}
                      {formData.integration_type === 'edesigntrade' && (
                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '6px', lineHeight: '1.7' }}>
                          <b>Параметры e-designtrade (Kravet group):</b><br />
                          <code>request_delay</code> — пауза между запросами, сек (по умолч. <b>0.5</b>)<br />
                          <code>commit_batch_size</code> — коммит в БД каждые N артикулов (по умолч. <b>50</b>)<br />
                          <code>limit</code> — проверить только первые N артикулов (по умолч. все)<br />
                          <code>include_out_of_stock</code> — включать товары, которых уже нет на фабрике (по умолч. <b>false</b>)<br />
                          <code>debug</code> — подробные логи в консоль (по умолч. <b>false</b>)<br />
                          <code>use_proxy</code> — ходить через резидентный прокси (по умолч. <b>false</b>)<br />
                          Артикул через дробь (<code>BP11096/2</code>) = паттерн/цвет. Остаток в метрах или рулонах, с партиями и датой производства.<br />
                          Бренды: Kravet, Lee Jofa, GP&amp;J Baker, Baker Lifestyle, Mulberry, Ted Baker. Логин/пароль — от <b>e-designtrade.com</b>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Бренды */}
                <div className="form-group">
                  <label style={{ fontWeight: '600', display: 'block', marginBottom: '6px' }}>
                    Бренды
                    {formData.brand_ids.length > 0 && (
                      <span style={{ marginLeft: '8px', fontSize: '12px', color: '#6b7280', fontWeight: '400' }}>выбрано: {formData.brand_ids.length}</span>
                    )}
                  </label>
                  <small style={{ color: '#6b7280', fontSize: '12px', marginBottom: '8px', display: 'block' }}>Бренд может принадлежать только одной фабрике</small>
                  <input className="input" placeholder="Поиск бренда..." value={brandSearch}
                    onChange={e => setBrandSearch(e.target.value)}
                    style={{ marginBottom: '8px' }} />
                  <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '4px' }}>
                    {filteredBrands.map(brand => {
                      const checked = formData.brand_ids.includes(brand.id);
                      const otherFactory = !checked && brand.factory_id && brand.factory_id !== editingFactory?.id;
                      return (
                        <label key={brand.id} style={{
                          display: 'flex', alignItems: 'center', gap: '8px',
                          padding: '6px 8px', borderRadius: '4px', cursor: 'pointer',
                          background: checked ? '#eff6ff' : 'transparent',
                          opacity: otherFactory ? 0.5 : 1,
                        }}>
                          <input type="checkbox" checked={checked}
                            onChange={() => !otherFactory && toggleBrand(brand.id)}
                            disabled={otherFactory} />
                          <span style={{ fontSize: '13px' }}>{brand.name}</span>
                          {otherFactory && <span style={{ fontSize: '11px', color: '#9ca3af', marginLeft: 'auto' }}>другая фабрика</span>}
                        </label>
                      );
                    })}
                  </div>
                  {formData.brand_ids.length > 0 && (
                    <button type="button" onClick={() => setFormData({ ...formData, brand_ids: [] })}
                      style={{ marginTop: '6px', background: 'none', border: 'none', color: '#6b7280', fontSize: '12px', cursor: 'pointer', padding: '0', textDecoration: 'underline' }}>
                      Снять все
                    </button>
                  )}
                </div>

                {/* Последняя синхронизация */}
                {editingFactory?.config?.last_sync && (() => {
                  const ls = editingFactory.config.last_sync;
                  const finishedAt = ls.finished_at ? new Date(ls.finished_at).toLocaleString('ru') : '—';
                  return (
                    <div className="form-group" style={{ marginTop: '4px' }}>
                      <label style={{ fontWeight: '600', display: 'block', marginBottom: '6px' }}>Последняя синхронизация</label>
                      <div style={{ fontSize: '13px', color: '#374151', background: '#f9fafb', borderRadius: '6px', padding: '10px 12px', border: '1px solid #e5e7eb' }}>
                        <div style={{ marginBottom: '4px', color: '#6b7280' }}>{finishedAt}</div>
                        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                          <span>Проверено: <b>{ls.total_products}</b></span>
                          <span>Обновлено: <b>{ls.updated}</b></span>
                          <span>Изменилось: <b>{ls.changed}</b></span>
                          <span>Не найдено: <b>{ls.not_found}</b></span>
                          {ls.new_lookups > 0 && <span style={{ color: '#6b7280' }}>Новых артикулов: <b>{ls.new_lookups}</b></span>}
                          {ls.errors > 0 && <span style={{ color: '#ef4444' }}>Ошибок: <b>{ls.errors}</b></span>}
                        </div>
                        {ls.error_skus && ls.error_skus.length > 0 && (
                          <div style={{ marginTop: '8px', color: '#ef4444', fontSize: '12px' }}>
                            <div style={{ fontWeight: '500', marginBottom: '2px' }}>Артикулы с ошибками:</div>
                            <div style={{ wordBreak: 'break-all' }}>{ls.error_skus.join(', ')}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

              </div>
              <div className="modal-footer" style={{ flexShrink: 0 }}>
                <button type="button" className="btn btn-secondary" onClick={closeModal}>Отмена</button>
                <button type="submit" className="btn btn-primary" disabled={isSaving || !!configError}>
                  {isSaving ? 'Сохранение...' : (editingFactory ? 'Сохранить' : 'Создать')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Test SKU modal */}
      {testModal && (
        <div className="modal-overlay" onClick={() => setTestModal(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '560px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Тест артикула — {testModal.factoryName}</h2>
              <button className="modal-close" onClick={() => setTestModal(null)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                <input
                  className="input"
                  placeholder="Артикул, например W7551-02"
                  value={testSku}
                  onChange={e => setTestSku(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleTestSku()}
                  style={{ flex: 1 }}
                  autoFocus
                />
                <button className="btn btn-primary" onClick={handleTestSku} disabled={testLoading || !testSku.trim()}>
                  {testLoading ? 'Проверяю...' : 'Проверить'}
                </button>
              </div>

              {testResult && (
                <div style={{ marginTop: '4px' }}>
                  {testResult.error ? (
                    <div style={{ color: '#dc2626', fontSize: '13px', padding: '8px 10px', background: '#fee2e2', borderRadius: '6px' }}>
                      {testResult.error}
                    </div>
                  ) : (() => {
                    const r = testResult.result;
                    const isSdg = testResult.factory_sku !== undefined;
                    const isOmexco = testResult.integration_type === 'omexco';
                    const isKhroma = testResult.integration_type === 'khroma';
                    const isTexdecor = testResult.integration_type === 'texdecor';
                    const isElitis = testResult.integration_type === 'elitis';
                    const isDG = testResult.integration_type === 'designers_guild';
                    const isPF = testResult.integration_type === 'pierre_frey';
                    const S = { fontSize: '13px', color: '#374151' };
                    const L = { fontSize: '11px', color: '#9ca3af', marginRight: '4px' };
                    const row = (label, value) => (
                      <span style={{ marginRight: '16px', whiteSpace: 'nowrap' }}>
                        <span style={L}>{label}</span><span style={S}>{value}</span>
                      </span>
                    );

                    if (isOmexco) {
                      return (
                        <div style={{ fontSize: '13px' }}>
                          {testResult.message && (
                            <div style={{ color: '#92400e', background: '#fef3c7', padding: '6px 10px', borderRadius: '6px', marginBottom: '8px', fontSize: '12px' }}>
                              {testResult.message}
                            </div>
                          )}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 0', padding: '8px 10px', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                            {r
                              ? <>
                                  {row('наличие ', <b style={{ color: r.in_stock ? '#15803d' : '#dc2626' }}>{r.in_stock ? 'есть' : 'нет'}</b>)}
                                  {r.quantity != null && row('кол-во ', `${r.quantity} м`)}
                                </>
                              : row('результат ', <span style={{ color: '#6b7280' }}>не найден</span>)
                            }
                          </div>
                        </div>
                      );
                    }

                    if (isKhroma) {
                      return (
                        <div style={{ fontSize: '13px' }}>
                          {testResult.message && (
                            <div style={{ color: r?.in_stock === false ? '#92400e' : '#dc2626', background: r?.in_stock === false ? '#fef3c7' : '#fee2e2', padding: '6px 10px', borderRadius: '6px', marginBottom: '8px', fontSize: '12px' }}>
                              {testResult.message}
                            </div>
                          )}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 0', padding: '8px 10px', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                            {r != null
                              ? <>
                                  {row('наличие ', <b style={{ color: r.in_stock ? '#15803d' : '#dc2626' }}>{r.in_stock ? 'есть' : 'нет'}</b>)}
                                  {r.in_stock && r.quantity != null && row('остаток ', <span>{r.quantity >= 20 ? 'больше 20' : r.quantity >= 10 ? '10–20' : r.quantity >= 5 ? '5–10' : '1–5'}</span>)}
                                </>
                              : row('результат ', <span style={{ color: '#6b7280' }}>ошибка</span>)
                            }
                          </div>
                        </div>
                      );
                    }

                    if (isTexdecor) {
                      return (
                        <div style={{ fontSize: '13px' }}>
                          {testResult.message && (
                            <div style={{ color: '#92400e', background: '#fef3c7', padding: '6px 10px', borderRadius: '6px', marginBottom: '8px', fontSize: '12px' }}>
                              {testResult.message}
                            </div>
                          )}
                          {r ? (
                            <div style={{ padding: '8px 10px', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 0', marginBottom: r.batches?.length ? '8px' : 0 }}>
                                {row('наличие ', <b style={{ color: r.in_stock ? '#15803d' : '#dc2626' }}>{r.in_stock ? 'есть' : 'нет'}</b>)}
                                {r.quantity != null && row('итого ', `${r.quantity} ML`)}
                              </div>
                              {r.batches?.length > 0 && (
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                  <thead>
                                    <tr style={{ borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>
                                      <th style={{ textAlign: 'left', padding: '2px 8px 4px 0', fontWeight: 500 }}>Партия</th>
                                      <th style={{ textAlign: 'right', padding: '2px 0 4px', fontWeight: 500 }}>Кол-во ML</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {r.batches.map((b, idx) => (
                                      <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                        <td style={{ padding: '3px 8px 3px 0', color: '#374151' }}>{b.batch}</td>
                                        <td style={{ padding: '3px 0', textAlign: 'right', color: '#374151' }}>{b.quantity}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          ) : row('результат ', <span style={{ color: '#6b7280' }}>не найден</span>)}
                        </div>
                      );
                    }

                    if (isElitis) {
                      return (
                        <div style={{ fontSize: '13px' }}>
                          {testResult.message && (
                            <div style={{ color: r?.in_stock === false ? '#92400e' : '#dc2626', background: r?.in_stock === false ? '#fef3c7' : '#fee2e2', padding: '6px 10px', borderRadius: '6px', marginBottom: '8px', fontSize: '12px' }}>
                              {testResult.message}
                            </div>
                          )}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 0', padding: '8px 10px', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                            {r
                              ? <>
                                  {row('наличие ', <b style={{ color: r.in_stock ? '#15803d' : '#dc2626' }}>{r.in_stock ? 'есть' : 'нет'}</b>)}
                                  {r.quantity != null && row('кол-во ', `${r.quantity} ${r.unit || ''}`)}
                                  {r.incoming_date && row('ожидается ', <b style={{ color: '#92400e' }}>{new Date(r.incoming_date + 'T00:00:00').toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}</b>)}
                                </>
                              : row('результат ', <span style={{ color: '#6b7280' }}>не найден</span>)
                            }
                          </div>
                        </div>
                      );
                    }

                    if (isDG) {
                      return (
                        <div style={{ fontSize: '13px' }}>
                          {testResult.message && (
                            <div style={{ color: '#92400e', background: '#fef3c7', padding: '6px 10px', borderRadius: '6px', marginBottom: '8px', fontSize: '12px' }}>
                              {testResult.message}
                            </div>
                          )}
                          {r ? (
                            <div style={{ padding: '8px 10px', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 0', marginBottom: r.batches?.length ? '8px' : 0 }}>
                                {row('наличие ', <b style={{ color: r.in_stock ? '#15803d' : '#dc2626' }}>{r.in_stock ? 'есть' : 'нет'}</b>)}
                                {r.quantity != null && row('итого ', `${r.quantity} м`)}
                              </div>
                              {r.batches?.length > 0 && (
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                  <thead>
                                    <tr style={{ borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>
                                      <th style={{ textAlign: 'left', padding: '2px 8px 4px 0', fontWeight: 500 }}>Партия</th>
                                      <th style={{ textAlign: 'right', padding: '2px 0 4px', fontWeight: 500 }}>Итого</th>
                                      <th style={{ textAlign: 'right', padding: '2px 0 4px 8px', fontWeight: 500 }}>Куски</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {r.batches.map((b, idx) => (
                                      <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                        <td style={{ padding: '3px 8px 3px 0', color: '#374151' }}>{b.batch}</td>
                                        <td style={{ padding: '3px 0', textAlign: 'right', color: '#374151' }}>{b.quantity} м</td>
                                        <td style={{ padding: '3px 0 3px 8px', textAlign: 'right', color: '#6b7280', fontSize: '11px' }}>
                                          {b.pieces ? b.pieces.map(p => `${p}м`).join(', ') : '—'}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          ) : row('результат ', <span style={{ color: '#6b7280' }}>нет данных</span>)}
                        </div>
                      );
                    }

                    if (isPF) {
                      return (
                        <div style={{ fontSize: '13px' }}>
                          {testResult.message && (
                            <div style={{ color: '#92400e', background: '#fef3c7', padding: '6px 10px', borderRadius: '6px', marginBottom: '8px', fontSize: '12px' }}>
                              {testResult.message}
                            </div>
                          )}
                          {testResult.uuid && (
                            <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '6px' }}>
                              UUID: <code style={{ color: '#6b7280' }}>{testResult.uuid}</code>
                            </div>
                          )}
                          {r ? (
                            <div style={{ padding: '8px 10px', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 0', marginBottom: r.batches?.length ? '8px' : 0 }}>
                                {row('наличие ', <b style={{ color: r.in_stock ? '#15803d' : '#dc2626' }}>{r.in_stock ? 'есть' : 'нет'}</b>)}
                                {r.quantity != null && row('итого ', `${r.quantity} м`)}
                              </div>
                              {r.batches?.length > 0 && (
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                  <thead>
                                    <tr style={{ borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>
                                      <th style={{ textAlign: 'left', padding: '2px 8px 4px 0', fontWeight: 500 }}>Партия</th>
                                      <th style={{ textAlign: 'right', padding: '2px 0 4px', fontWeight: 500 }}>Итого</th>
                                      <th style={{ textAlign: 'right', padding: '2px 0 4px 8px', fontWeight: 500 }}>Куски</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {r.batches.map((b, idx) => (
                                      <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                        <td style={{ padding: '3px 8px 3px 0', color: '#374151' }}>{b.batch}</td>
                                        <td style={{ padding: '3px 0', textAlign: 'right', color: '#374151' }}>{b.quantity} м</td>
                                        <td style={{ padding: '3px 0 3px 8px', textAlign: 'right', color: '#6b7280', fontSize: '11px' }}>
                                          {b.pieces ? b.pieces.map(p => `${p}м`).join(', ') : '—'}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          ) : row('результат ', <span style={{ color: '#6b7280' }}>нет данных</span>)}
                        </div>
                      );
                    }

                    if (isSdg) {
                      const inStock = r?.in_stock;
                      const batches = r?.batches || [];
                      return (
                        <div style={{ fontSize: '13px' }}>
                          {testResult.message && (
                            <div style={{ color: '#92400e', background: '#fef3c7', padding: '6px 10px', borderRadius: '6px', marginBottom: '8px', fontSize: '12px' }}>
                              {testResult.message}
                            </div>
                          )}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 0', padding: '8px 10px', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                            {row('арт. фабрики ', <code style={{ fontFamily: 'monospace', fontSize: '12px' }}>{testResult.factory_sku || '—'}</code>)}
                            {r && row('наличие ', <b style={{ color: inStock ? '#15803d' : '#dc2626' }}>{inStock ? 'есть' : 'нет'}</b>)}
                            {r?.quantity != null && row('кол-во ', r.quantity)}
                            {r?.batch && row('партия ', r.batch)}
                            {r?.incoming_date && row('производство ', <b style={{ color: '#92400e' }}>{new Date(r.incoming_date + 'T00:00:00').toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}{r?.incoming_quantity ? `, ${Number.isInteger(r.incoming_quantity) ? r.incoming_quantity : r.incoming_quantity.toFixed(1)} рул.` : ''}</b>)}
                          </div>
                          {batches.length > 0 && (
                            <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                              {batches.map((b, i) => (
                                <span key={i} style={{ fontSize: '12px', background: '#f3f4f6', padding: '2px 8px', borderRadius: '4px', color: '#374151' }}>
                                  {b.batch}{b.quantity != null ? ` × ${b.quantity}` : ''}
                                </span>
                              ))}
                            </div>
                          )}
                          {testResult.debug && (
                            <details style={{ marginTop: '8px' }}>
                              <summary style={{ fontSize: '11px', color: '#9ca3af', cursor: 'pointer' }}>debug</summary>
                              <pre style={{ fontSize: '11px', color: '#6b7280', background: '#f9fafb', padding: '8px', borderRadius: '4px', margin: '4px 0 0', overflow: 'auto', maxHeight: '200px', border: '1px solid #e5e7eb' }}>
                                {JSON.stringify(testResult.debug, null, 2)}
                              </pre>
                            </details>
                          )}
                        </div>
                      );
                    }

                    // OL format
                    return (
                      <div style={{ fontSize: '13px' }}>
                        <div style={{ padding: '8px 10px', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                          {row('найден ', <b style={{ color: r?.found ? '#15803d' : '#dc2626' }}>{r?.found ? 'да' : 'нет'}</b>)}
                        </div>
                        {r?.found && (
                          <pre style={{ marginTop: '6px', fontSize: '11px', color: '#6b7280', background: '#f9fafb', padding: '8px', borderRadius: '4px', overflow: 'auto', maxHeight: '200px', border: '1px solid #e5e7eb' }}>
                            {JSON.stringify(r.data, null, 2)}
                          </pre>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setTestModal(null)}>Закрыть</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
